import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * MonthlyHeatmap — reads localStorage prayer data for the current month
 * and renders a colour-coded calendar grid.
 */
const MonthlyHeatmap = () => {
  const { t } = useTranslation();
  const { isDarkMode, language, isRTL } = useTheme();
  const [monthData, setMonthData] = useState({});

  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth(); // 0-indexed
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = new Date(year, month, 1).getDay(); // 0 = Sun

  // Read localStorage for this month
  useEffect(() => {
    const data = {};
    for (let d = 1; d <= daysInMonth; d++) {
      const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const stored = localStorage.getItem(`prayers_${key}`);
      if (stored) {
        const prayers = JSON.parse(stored);
        data[d] = Object.values(prayers).filter(Boolean).length;
      }
    }
    setMonthData(data);
  }, [year, month, daysInMonth]);

  const getDayColor = (day) => {
    if (day > today.getDate() && month === today.getMonth() && year === today.getFullYear()) {
      return isDarkMode ? 'bg-white/5 text-gray-700' : 'bg-gray-50 text-gray-300';
    }
    const count = monthData[day];
    if (count === undefined) return isDarkMode ? 'bg-white/8 text-gray-500' : 'bg-gray-100 text-gray-400';
    if (count === 5) return 'bg-emerald-500 text-white';
    if (count >= 3) return isDarkMode ? 'bg-emerald-500/40 text-emerald-300' : 'bg-emerald-200 text-emerald-800';
    if (count >= 1) return isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600';
    return isDarkMode ? 'bg-red-500/15 text-red-400' : 'bg-red-50 text-red-400';
  };

  const isToday = (day) =>
    day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  // Build calendar grid cells
  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null); // empty leading cells
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const dayLabels = language === 'ar'
    ? ['أح', 'إث', 'ثل', 'أر', 'خم', 'جم', 'سب']
    : ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  // Completion % for the month so far
  const pastDays = Math.min(today.getDate(), daysInMonth);
  const completedDays = Object.values(monthData).filter(c => c === 5).length;
  const monthRate = pastDays > 0 ? Math.round((completedDays / pastDays) * 100) : 0;

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Month header + rate */}
      <div className={`flex items-center justify-between mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <span className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          {today.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { month: 'long', year: 'numeric' })}
        </span>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
          monthRate >= 80 ? 'bg-emerald-500/20 text-emerald-500'
          : monthRate >= 50 ? isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'
          : isDarkMode ? 'bg-white/10 text-gray-400' : 'bg-gray-100 text-gray-500'
        }`}>
          {monthRate}% {t('tracker.completionRate')}
        </span>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 mb-1">
        {dayLabels.map((d, i) => (
          <div key={i} className={`text-center text-[10px] font-semibold pb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`}>
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => (
          <div
            key={i}
            className={`aspect-square rounded-lg flex items-center justify-center text-xs font-semibold transition-colors ${
              day === null ? '' : getDayColor(day)
            } ${day && isToday(day) ? 'ring-2 ring-[var(--p-hex)] ring-offset-1' : ''}`}
            style={day && isToday(day) ? { '--tw-ring-offset-color': isDarkMode ? '#0f172a' : 'white' } : undefined}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className={`flex items-center gap-3 mt-3 text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-400'} ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-emerald-500" />
          <span>5/5</span>
        </div>
        <div className="flex items-center gap-1">
          <div className={`w-3 h-3 rounded ${isDarkMode ? 'bg-emerald-500/35' : 'bg-emerald-200'}`} />
          <span>3-4</span>
        </div>
        <div className="flex items-center gap-1">
          <div className={`w-3 h-3 rounded ${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'}`} />
          <span>1-2</span>
        </div>
        <div className="flex items-center gap-1">
          <div className={`w-3 h-3 rounded ${isDarkMode ? 'bg-red-500/15' : 'bg-red-50'}`} />
          <span>0</span>
        </div>
      </div>
    </div>
  );
};

export default MonthlyHeatmap;
